/*
 * $Id:: FooterMobile.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector } from 'react-redux';

import { globals as globalsSlice } from '../../app/globalsSlice';
import { ReactComponent as EUIPNLogo } from '../../assets/images/euipn_logo.svg';
import { ReactComponent as LinkedInIcon } from '../../assets/images/linkedIn_icon.svg';
import { ReactComponent as TwitterIcon } from '../../assets/images/twitter_icon.svg';
import { ReactComponent as YoutubeIcon } from '../../assets/images/youtube_icon.svg';
import { t } from '../../providers/TranslationProvider';
import ExternalLinks from '../ExternalLinks';
import ExpandedSection from './ExpandedSection';

const useStyles = makeStyles((theme) => ({
    footer: {
        backgroundColor: theme.palette.action.dimGrey,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '30px 21vw',
        '@media (max-width:600px)': {
            padding: '30px 16px',
        },
        '& svg': {
            fill: theme.palette.text.outlined,
        },
    },
    expandedSection: {
        marginTop: '20px',
    },
    expansionPanel: {
        backgroundColor: 'transparent',
        boxShadow: 'unset',
    },
    expansionPanelSummary: {
        borderBottom: '3px solid' + theme.palette.text.outlined,
        padding: 'unset',
    },
    expandedSectionTitle: {
        color: theme.palette.primary.main,
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
    },
    expandedSectionContent: {
        padding: '8px 0 0 0',
    },
    footerLinkLg: {
        color: '#333333',
        fontSize: '14px',
        fontWeight: 'normal',
        textDecorationLine: 'underline',
        paddingBottom: '5px',
        cursor: 'pointer',
    },
    info: {
        color: '#ffffff',
    },
    infoItem: {
        lineHeight: '20px',
        fontStyle: 'normal',
        fontWeight: '600',
        fontSize: '14px',
        letterSpacing: 'normal',
        color: '#ffffff',
    },
    footerversion: {
        float: 'right',
        fontSize: '12px',
        opacity: '.5',
    },
    informationLinksCol: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
    },
    section: {
        marginTop: '30px',
    },
    social: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '18px',
    },
    socialBtn: {
        paddingRight: '20px',
    },
    logo: {
        display: 'flex',
        justifyContent: 'center',
        '@media (max-width:600px)': {
            justifyContent: 'flex-start',
        },
        '& svg': {
            maxWidth: '383px',
            width: '100%',
        },
    },
    appVersion: {
        textAlign: 'center',
    },
}));

const useStylesFooterTitle = makeStyles((theme) => ({
    title: {
        color: '#455A64',
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        borderBottom: '3px solid' + theme.palette.text.outlined,
        paddingBottom: '15px',
    },
}));

const FooterTitle = ({ title }) => {
    const classes = useStylesFooterTitle();
    return <Typography className={classes.title}>{title}</Typography>;
};

const FooterMobile = () => {
    const classes = useStyles();
    const { globals } = useSelector(globalsSlice) || {};

    const preventDefault = (event) => event.preventDefault();

    const footerExternalLinks = globals && globals.footer && Object.values(globals.footer);

    return (
        <div className={classes.footer}>
            {/* VPBLogo */}
            <div className={classes.logo}>
                <EUIPNLogo />
            </div>

            {/* Find us here */}
            <div className={classes.section}>
                <FooterTitle title={t('find.us.here')} />
                <Typography className={`${classes.social} ${classes.section}`}>
                    <a href="#youtube" onClick={preventDefault} className={classes.socialBtn}>
                        <YoutubeIcon />
                    </a>
                    <a href="#twitter" onClick={preventDefault} className={classes.socialBtn}>
                        <TwitterIcon />
                    </a>
                    <a href="https://www.linkedin.com/company/27102388" onClick={preventDefault} className={classes.socialBtn}>
                        <LinkedInIcon />
                    </a>
                </Typography>
            </div>

            {/* About SPB */}
            <ExpandedSection
                classes={classes}
                title={t('about.vpb')}
                content={
                    <Typography className={`${classes.informationLinksCol}`}>
                        <ExternalLinks links={footerExternalLinks} className={classes.footerLinkLg} />
                    </Typography>
                }
            />

            {/* Contact information */}
            <ExpandedSection
                classes={classes}
                title={t('contact.information.title')}
                content={
                    <div className={`${classes.info}`}>
                        <Typography variant={'subtitle2'} className={classes.infoItem} component={'div'}>
                            {t('footer.address')}
                        </Typography>
                        <Typography variant={'subtitle2'} className={classes.infoItem} component={'div'}>
                            {t('information.centre.phone')}: +370 5 278 0290
                        </Typography>
                        <Typography variant={'subtitle2'} className={classes.infoItem} component={'div'}>
                            {t('information.centre.email')}: info@vpb.gov.lt
                        </Typography>
                        <Typography variant={'subtitle2'} className={classes.infoItem} component={'div'}>
                            {t('information.centre.feedback')}
                        </Typography>
                    </div>
                }
            />

            {/* Legal information */}
            <ExpandedSection
                classes={classes}
                title={t('legal.information')}
                content={
                    <div className={`${classes.info}`}>
                        <Typography variant={'subtitle2'} className={classes.footerLinkLg} component={'div'}>
                            <a href={`https://vpb.lrv.lt/asmens-duomenu-apsauga`}
                                    className={`${classes.informationLinksCol} ${classes.section}`} target={'_blank'}>
                                {t('legal.info')}
                            </a>
                        </Typography>
                    </div>
                }
            />

            {/* Version */}
            <span className={[classes.footerversion, classes.section, classes.appVersion].join(' ')} id="footer_app_version">
                {globals && globals.ipo && globals.ipo.version && `${t('version')}: ${globals.ipo.version}`}
            </span>
        </div>
    );
};

export default FooterMobile;
